/* screens/Clark/answer/answerV2.css — RUN 5 · PARTS 2 and 4.
 *
 * 🔴 EVERY COLOUR IN THIS FILE IS A TOKEN, AND NO TOKEN CARRIES A LITERAL FALLBACK.
 * BUNDLE E's recorded finding is that a literal second argument to var() IS THE VALUE in any theme
 * that does not define the token — so a "safety" fallback is precisely how a hard-coded colour
 * ships past a raw-colour gate. If a token is ever missing, the right outcome is an unstyled
 * element somebody notices, not a wrong-coloured one nobody does.
 *
 * The colour tokens used here are defined in every one of the 19 theme blocks in
 * packages/ui/themes.css and public/themes.css.
 *
 * 🔴 `--font-body` WAS NEVER ONE OF THEM, AND THAT LINE OF THIS HEADER WAS THE BUG.
 * It said "`--font-body` comes from public/styles.css" — which is true, and is exactly why three
 * declarations in this file did nothing. `apps/main/public/styles.css` is the MARKETING
 * stylesheet; this SPA never loads it. Probed live on /clark,
 * `getComputedStyle(document.documentElement).getPropertyValue("--font-body")` returns THE EMPTY
 * STRING, so `font-family: var(--font-body)` is an invalid declaration and those elements
 * inherited whatever face surrounded them. They looked correct by accident for as long as
 * everything around them was the body font. RUN VA changed the prose to a text serif, at which
 * point an inherited font is a different font, and repaired all three to `--cc-font-body` —
 * which IS defined, in client/src/styles/tokens.css, on the chain this app actually loads.
 * ⭐ A token that resolves to nothing fails SILENTLY and in the direction of "looks fine".
 *
 * 🔴 RUN VA also sets two font FAMILIES literally rather than through a token — see the pairing
 * block below for why (tokens.css is not that run's file) and what would replace them.
 *
 * There are 19 themes and this was not eyeballed in all of them; it is built from the same tokens
 * the surrounding answer already uses, which is what makes that survivable. RUN VA's contrast
 * sweep did measure all 19, both declared and painted: ops/run-va/out/contrast-after.json.
 */

/* ══ 🔴 RUN VA — THE LEDGER'S RULES, DELETED WITH THE LEDGER (RUN VB's handoff) ════════════════
 *
 * RUN VB removed the "How we know" line and both See-also blocks from `ClarkAnswerV2.jsx`
 * (07f8cf3f). VB owns no CSS and handed this file the orphan list; it was re-derived here against
 * the CURRENT tree rather than taken on trust — 2,246 live source files scanned with `dist/`,
 * `.build/`, `public/` and every ops harness excluded, because a built bundle contains every one
 * of these class names and counting it would report every orphan as still in use.
 *
 * DELETED, all with no live renderer:
 *     .cc-v2-howknow · .cc-v2-howknow b · .cc-v2-howknow-caveat · .cc-v2-foot
 *     .cc-v2-seealso-head · .cc-v2-stripped .cc-v2-seealso-list · .cc-v2-seealso-list > .cc-auth
 *     .cc-v2-seealso-list > .cc-auth:hover · .cc-v2-seealso-list > .cc-auth .cc-auth__nm
 *
 * 🔴 KEPT, AND THE DISTINCTION IS THE WHOLE POINT OF CHECKING: `.cc-v2-stripped` and
 * `.cc-v2-stripped-head` are STILL LIVE — `GroundedAnswer.jsx:106-107` renders them for a
 * different message entirely ("This document was too long to send whole"). Deleting them because
 * they sat between the two deleted groups would have unstyled a surface nobody in this arc was
 * looking at, and the guard that caught me trying is in ops/run-va/REPORT.md.
 *
 * ⚠️ AND THE PIN WAS INVERTED IN THIS COMMIT, NOT AFTER IT. `clarkBadgeRemoved.test.js` asserted
 * `/\.cc-v2-howknow\b/.test(css)` must be TRUE — "the line that replaced it must be styled". VB
 * left it standing deliberately and said so in the test file itself. Deleting the rule without
 * inverting the assertion lands red; inverting it in a LATER commit leaves a window in which the
 * branch is broken. Both are one commit.
 *
 * ⭐ ONE MEASURED CONSEQUENCE, RECORDED BECAUSE IT WAS PAID FOR: this run's contrast sweep found
 * `.cc-v2-howknow-caveat` (`--cc-ink-disabled` on `--cc-stage`) BELOW the 4.5:1 body bar on two of
 * nineteen themes — m3-yellow 4.15:1 and m2-green 4.43:1, identical in both arms, so pre-existing
 * and not this run's. The deletion above retires it. Had the ledger stayed, that is a real defect
 * and the fix is one token.
 */


/* ── an authority the corpus could not confirm ────────────────────────────────────────────────
 *
 * A <mark> with no yellow. The default UA highlight is a background nobody chose and it is
 * illegible in half the dark themes.
 */
.cc-v2-unresolved {
  background: transparent;
  color: inherit;
  /* Dotted, not a solid red squiggle: this is "we could not confirm this", not "this is wrong". */
  border-bottom: 1px dotted var(--cc-border-strong);
  text-decoration: none;
  cursor: help;
}

/* RUN C · PART 3 — "we could not look", which is not "it is not there". Dotted like the unresolved
   mark because both are non-verdicts, but the dot pattern differs so the two are distinguishable
   without reading the tooltip. */
.cc-v2-unchecked {
  background: transparent;
  color: inherit;
  border-bottom: 1px dashed var(--cc-border-soft);
  text-decoration: none;
  cursor: help;
}

/* A quotation we withdrew. Deliberately NOT styled like either mark above: those say "we could not
   confirm", this says "we checked and it failed", and a reader must tell them apart at a glance. */
.cc-v2-unsupported {
  font-family: var(--cc-font-mono, ui-monospace, monospace);
  font-size: 0.78em;
  letter-spacing: 0.02em;
  color: var(--cc-ink-muted);
  border: 1px solid var(--cc-border-soft);
  border-radius: 3px;
  padding: 1px 5px;
  margin: 0 2px;
  white-space: nowrap;
  cursor: help;
  vertical-align: baseline;
}

/* RUN C · AMENDMENT — the placeholder that keeps the control row three equal thirds when one of the
   last two controls does not apply. It paints nothing; it only holds its grid cell open. */
.cc-v2-action-slot { display: block; }

.cc-v2-stripped {
  margin-top: 0.9rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid var(--cc-border-soft);
  border-radius: 8px;
  background: var(--cc-bg-subtle);
  font-size: 0.86rem;
}
.cc-v2-stripped-head { margin: 0 0 0.35rem; color: var(--cc-ink); }
.cc-v2-stripped ul { margin: 0; padding-left: 1.1rem; color: var(--cc-ink-muted); }


/* ── PART 4 · the three controls ──────────────────────────────────────────────────────────────
 *
 * 🔴 GRID, NOT FLEX. The brief asks for equal widths, each one third of the answer's available
 * width. `display:flex` + `flex:1` sizes each child to its CONTENT before distributing the
 * remainder, so three buttons only LOOK equal while their labels are a similar length — and
 * "Show Authorities" is half again as long as "Copy Answer". `1fr 1fr 1fr` is thirds by
 * construction, whatever the labels say.
 */
.cc-v2-actions {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.75rem;
}

.cc-v2-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* 44px minimum touch target — the brief's floor, and it applies to these too. */
  min-height: 44px;
  padding: 0 0.75rem;
  border: 1px solid var(--cc-border);
  border-radius: 8px;
  background: var(--cc-bg-card);
  color: var(--cc-ink);
  font: inherit;
  font-size: 0.86rem;
  font-family: var(--cc-font-body);
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;
}

.cc-v2-action:hover:not(:disabled) { background: var(--cc-card-hover); }
.cc-v2-action:focus-visible { outline: 2px solid var(--cc-accent); outline-offset: 2px; }

/* The latter two take the accent fill, per the brief. `Copy Answer` stays quiet: it is the one
   control that does not move the user anywhere. */
.cc-v2-action--accent {
  background: var(--cc-accent-fill);
  border-color: var(--cc-accent-fill);
  color: var(--cc-on-accent);
}
.cc-v2-action--accent:hover:not(:disabled) {
  background: var(--cc-accent-fill-hover);
  border-color: var(--cc-accent-fill-hover);
}

/* 🔴 A DISABLED CONTROL MUST LOOK DISABLED. `Open in Research` with no stored answer keeps its
   slot (so the row stays in thirds) and stops claiming to be available. */
.cc-v2-action:disabled { opacity: 0.45; cursor: not-allowed; }

.cc-v2-action.is-on { filter: brightness(0.94); }

/* ── the authority list ───────────────────────────────────────────────────────────────────────*/
.cc-v2-authorities {
  margin-top: 0.85rem;
  padding-top: 0.85rem;
  border-top: 1px solid var(--cc-border-soft);
}

.cc-v2-authorities-q {
  margin: 0 0 0.7rem;
  color: var(--cc-ink-muted);
  font-size: 0.85rem;
  font-family: var(--cc-font-body);
}

/* 🔴 RUN CR · BLOCK 7 — THE LIST STOPS BEING A NUMBERED LIST, BECAUSE RESEARCH'S IS NOT ONE.
   Adam: *"Needs to look 1:1 with Research."* The `<ol>` marker was the last thing drawing a
   numeral beside each authority — `.cc-v2-auth-rank` was already `display:none` for exactly that
   reason ("the <ol> already numbers it — twice is noise"), and now the marker goes the same way.
   ⭐ THE ELEMENT STAYS AN `<ol>` AND THAT IS DELIBERATE: the payload's order is meaningful (it is
   the answer's own ranking), so the ORDERED semantics are correct for a screen reader even though
   the numeral is not drawn. `list-style: none` removes the marker, not the ordering.
   The gap is Research's rail gap; the cards bring their own frame. */
.cc-v2-auth-list { margin: 0; padding-left: 0; list-style: none; display: grid; gap: 10px; }
/* The card, on the same terms as Clark's rail and Research's — see answer.css's shadow block. */
.cc-v2-auth-list .cc-auth { box-shadow: var(--cc-shadow-md); }
/* The absent-`why` voice, moved from the deleted `<p>` onto the card's own slot. RUN 9 · PART 6's
   distinction survives: a row whose sentence is absent still says so, quietly and in italic. */
.cc-v2-auth[data-why="absent"] .cc-auth__why { color: var(--cc-text-disabled); font-style: italic; }
.cc-v2-auth-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 0.45em; }
.cc-v2-auth-rank { display: none; }            /* the <ol> already numbers it — twice is noise */
.cc-v2-auth-name { font-weight: 600; color: var(--cc-ink); }
a.cc-v2-auth-name:hover { color: var(--cc-accent); }
.cc-v2-auth-cite { color: var(--cc-ink-muted); font-variant-numeric: tabular-nums; }
.cc-v2-auth-kind {
  color: var(--cc-ink-muted);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.cc-v2-auth-why { margin: 0.25rem 0 0; color: var(--cc-ink-muted); font-size: 0.88rem; }
/* 🔴 The label is what stops this reading as a holding. See ClarkAnswerV2.jsx's AuthorityRow. */
.cc-v2-auth-why-label { color: var(--cc-ink); font-weight: 600; }

.cc-v2-provenance { margin-bottom: 0.6rem; }

/* 🔴🔴 RUN CK · A1 — TWO BUTTONS, TWO COLUMNS. THE PHONE ROW WAS THIRDS WITH A HOLE IN IT.
   `answer.css` hides `Show Authorities` below 600px (it becomes the `4 authorities` disclosure),
   but nothing narrowed the GRID: the two survivors kept 1fr of a THREE-column track and the empty
   third sat beside them. MEASURED at 430px-class widths before this rule — `Copy Answer` and
   `Open in Research` at ~1/3 of the row each, wrapping to two lines, ending well short of the
   disclosure and the message text above them. That is the defect Adam reported from the iPhone.
   MEASURED after: each button is exactly half, the pair spans the row, and the row's edges land on
   the message text's edges.
   ⚠️ `min-width: 0` IS LOAD-BEARING. A grid item's default `min-width: auto` is its CONTENT width,
   so `Open in Research` — the longer label, and the one this run makes LIVE rather than dulled,
   which changes its fill — would push its track past 1fr and shove the row wider than the text
   column. That is precisely the failure being fixed.
   ⚠️ EQUAL HEIGHTS ARE `align-items: stretch`, the grid default: one button wrapping takes the
   other with it rather than leaving a short one beside a tall one. "Even" needs both.
   ⚠️ THE <=420 STACK BELOW STILL WINS, DELIBERATELY, AND IT IS LATER ON PURPOSE. Half of a 320px
   column is ~96px, which is narrower than "Open in Research" — halves there would re-create the
   two-line wrap this rule exists to remove. Stacked, they are already equal width and equal
   height. So: 421-599.98 gets halves, <=420 keeps the stack.
   ⚠️ DESKTOP IS UNTOUCHED — this is inside a max-width query, and >=600px keeps its three thirds.
   599.98 is also BELOW the 641-767 band, so the band renders the desktop row either way. */
@media (max-width: 599.98px) {
  .cc-v2-actions { grid-template-columns: 1fr 1fr; }
  /* The empty geometry spans hold a THIRD cell open; with two columns one would eat half the row. */
  .cc-v2-actions .cc-v2-action-slot { display: none; }
  .cc-v2-actions .cc-v2-action { min-width: 0; }
}

/* Narrow viewports: thirds of a 320px column is three unreadable buttons. Stack instead — the
   "equal thirds" rule is about the answer's available width, and below this there isn't one. */
@media (max-width: 420px) {
  .cc-v2-actions { grid-template-columns: 1fr; }
}

/* RUN 9 · PART 6 — the why-slot when the model listed an authority it never named in prose.
   Quieter than a real `why`, because it is a statement about the PAYLOAD rather than a quotation
   from the answer, and it must not be mistaken for one. */
.cc-v2-auth-why--absent { color: var(--cc-text-disabled); font-style: italic; }

/* RUN 9 · PART 5.1 — the grounded door on an authority row. Text-weight, no fill: it is an action
   on a row, not a call to action. */
.cc-v2-auth-ask {
  margin-top: 4px; padding: 0; border: 0; background: none; cursor: pointer;
  font-size: 11.5px; font-weight: 600; color: var(--cc-brand);
  border-bottom: 1px solid transparent;
}
.cc-v2-auth-ask:hover { border-bottom-color: currentColor; }

/* ══ 🔴🔴 RUN VA — THE PAIRING. THE BODY IS NOT A DISPLAY FACE ANY MORE. ═══════════════════════
 *
 * Adam, on the live build: "the font looks like SHIT."
 *
 * 🔴 IT WAS NEVER THE WEIGHT, AND CHASING THE WEIGHT WOULD HAVE MADE IT WORSE. RUN RA measured
 * this prose at font-weight **400** already. What reads as bold is DM SERIF DISPLAY DOING
 * PARAGRAPH WORK — a display cut (high stroke contrast, tight sidebearings, small apertures,
 * drawn to be set once at 40px) carrying four hundred words at 19px. A display face set small
 * gets DENSER, not lighter, so "make it smaller" and "make it lighter" both point the wrong way.
 *
 * So the faces are PAIRED, not swapped. DM Serif Display keeps every display job it has — the
 * Ask Clark lockup, headings, the rail's cite line, and the case names below. Body prose moves to
 * Source Serif 4, a text cut with an optical-size axis (at 17.5px the browser instances the TEXT
 * master: looser spacing, lower contrast, larger apertures) and — the part Bluebook cares about —
 * a TRUE ITALIC rather than a slanted roman. See public/fonts/fonts.css for the files and bytes.
 *
 * 🔴 TWO CLASSES, AND THAT IS DELIBERATE, NOT SPECIFICITY PADDING. `answer.css` declares
 * `.cc-ans-prose` at 19px/1.55 in the display face. This file is imported AFTER it by
 * `ClarkAnswerV2.jsx`, so a single-class rule would win TODAY — but `ResearchAnswer.jsx` imports
 * `answer.css` WITHOUT this file, and the order two stylesheets land in a bundle is a property of
 * the import graph, not of these two lines. `.cc-clark-section-prose` is on the same element in
 * every renderer that emits this class (ClarkAnswerV2.jsx:229, ResearchAnswer.jsx:151/260), so
 * pinning on the pair wins by SPECIFICITY and cannot be silently reordered out of effect.
 *
 * ⚠️ NOT A TOKEN, AND THE REASON IS SCOPE. `--cc-font-display` / `--cc-font-body` live in
 * `client/src/styles/tokens.css`, which is not this run's file. The stack is therefore written
 * literally here, once per rule. When a second surface needs it — Research's own answer.css in
 * apps/steel is the obvious one — it becomes `--cc-font-text` in tokens.css and these become
 * `var()`s. Georgia is the fallback because it is the fallback `--cc-font-display` already
 * carries: a swap failure lands on a text serif, not on Times.
 *
 * ⭐ MEASURED, not asserted — 412 words, real compose, real Chrome, 1440:
 *      before  DM Serif Display 19px/29.45px  prose 708px  33 lines  1024.6px tall
 *      after   Source Serif 4  17.5px/28px    prose 760px  28 lines   833.0px tall   (-18.7%)
 *    which is the "too much scrolling" half of the complaint, in one number.
 */
.cc-clark-section-prose.cc-ans-prose {
  font-family: 'Source Serif 4', Georgia, serif;
  font-size: 17.5px;
  line-height: 1.6;
  font-weight: 400;
}

/* The decline is the answer — RUN RA's header says so and styles it in the answer's own type
   rather than in error type. That argument survives the pairing and requires this rule: leaving
   the two decline paragraphs in the display face would put a 19px display serif and a 17.5px text
   serif on the same surface, differing by which of two code paths produced the sentence. */
.cc-ans-decline .cc-ans-decline-head,
.cc-ans-decline .cc-ans-decline-body {
  font-family: 'Source Serif 4', Georgia, serif;
  font-size: 17.5px;
  line-height: 1.6;
  font-weight: 400;
}

/* ── RUN TA — Bluebook R2.1: the case name is italic. ────────────────────────────────────────────
   Stated rather than left to the UA's `<em>` default, because `answer.css` sets `font-style` on
   several of its own blocks and a future reset that catches `em` would silently un-italicise every
   case name in the product with nothing failing. The element is still `<em>` — this rule is the
   belt, the semantics are the braces.

   🔴 RUN VA — AND NOW THE FAMILY IS STATED TOO, BECAUSE IT STOPPED BEING INHERITED.
   Until this run the case name inherited the display face from the prose around it and the italic
   was DM Serif Display's. With the body in Source Serif 4 that inheritance now delivers the TEXT
   face, so keeping the case name in the display face is a declaration or it is nothing. This is
   the one place the two faces meet inside a single line, and it is on purpose: a case name is a
   proper noun with a citation behind it, and the display italic is what makes it read as one
   without a colour, a weight or an underline — none of which R2.1 asks for and all of which would
   claim it is a link. Size is INHERITED (17.5px), never restated: a case name that renders at a
   different size from the sentence containing it is a typographic error, not an emphasis. */
.cc-v2-casename {
  font-style: italic;
  font-family: var(--cc-font-display);
}
